import React from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { StepProps } from "@/components/multi-step-form";
import { Edit } from "lucide-react";
import {
  CATEGORY_NAMES,
  AmenitiesData,
} from "@/components/amenities-form/amenities-form.types";
import { useStatisticalAnalysis } from "@/hooks/useStatisticalAnalysis";
import { spaceService } from "@/services/space-service";
import {
  ReviewFieldWrapper,
  usePriceFieldAlert,
  useCapacityFieldAlert,
} from "@/components/statistical-alerts";
import { NeighborhoodMissingAlert } from "@/components/statistical-alerts";

export const reviewSchema = z.object({});

export type ReviewData = z.infer<typeof reviewSchema>;

interface ReviewStepProps extends StepProps {
  onGoToStep?: (stepIndex: number) => void;
  amenitiesData?: AmenitiesData | null;
  onSubmit?: () => void;
  onStatisticalDataReady?: (data: any) => void;
}

export const StepReview: React.FC<ReviewStepProps> = ({
  onGoToStep,
  amenitiesData,
  onStatisticalDataReady,
}) => {
  const { watch } = useFormContext();
  const formData = watch();

  const [neighborhood, setNeighborhood] = React.useState<string | null>(null);
  const [neighborhoodFetched, setNeighborhoodFetched] =
    React.useState<boolean>(false);

  const {
    data: statisticalData,
    error: statisticalError,
    isLoading: isStatisticalLoading,
    hasExtremeOutliers,
    hasNeighborhood,
    localSizeCapacityRatio,
  } = useStatisticalAnalysis({
    neighborhood,
    monthlyPrice: formData.monthlyPrice,
    capacity: formData.capacity,
    sizeM2: formData.size,
    enabled: Boolean(
      neighborhood &&
        formData.monthlyPrice &&
        formData.capacity &&
        formData.size,
    ),
  });

  React.useEffect(() => {
    const fetchNeighborhoodForAnalysis = async () => {
      if (formData.externalSpaceId && !neighborhood && !neighborhoodFetched) {
        try {
          const space = await spaceService.getSpaceById({
            spaceId: formData.externalSpaceId,
          });
          setNeighborhood(space.neighborhood);
        } catch (error) {
          console.error(
            "Failed to fetch neighborhood for review analysis:",
            error,
          );
        } finally {
          setNeighborhoodFetched(true);
        }
      }
    };

    fetchNeighborhoodForAnalysis();
  }, [formData.externalSpaceId, neighborhood, neighborhoodFetched]);

  React.useEffect(() => {
    if (onStatisticalDataReady) {
      onStatisticalDataReady({
        statisticalData,
        hasExtremeOutliers,
      });
    }
  }, [statisticalData, hasExtremeOutliers, onStatisticalDataReady]);

  const priceAlert = usePriceFieldAlert({
    monthlyPrice: formData.monthlyPrice,
    statisticalData,
    statisticalError,
    isLoading: isStatisticalLoading,
  });

  const capacityAlert = useCapacityFieldAlert({
    size: formData.size,
    capacity: formData.capacity,
    statisticalData,
    localSizeCapacityRatio,
  });

  const handleEditSection = (stepIndex: number) => {
    if (onGoToStep) {
      onGoToStep(stepIndex);
    }
  };

  const renderSpaceInfo = () => {
    if (!formData.spaceName && !formData.spaceId) return null;

    const displaySpaceName =
      formData.spaceName || `Espaço ID: ${formData.spaceId}`;

    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Dados da sala</h3>
          <button
            type="button"
            onClick={() => handleEditSection(0)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <Edit className="w-4 h-4" />
            Editar
          </button>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-4">
            {/* Nome do escritório - sempre normal */}
            <div>
              <span className="text-gray-600 text-sm">Nome do escritório</span>
              <p className="font-medium text-gray-900">{displaySpaceName}</p>
            </div>

            {formData.externalSpaceId &&
              neighborhoodFetched &&
              !hasNeighborhood && (
                <NeighborhoodMissingAlert
                  spaceId={formData.externalSpaceId}
                  spaceName={
                    formData.spaceName ||
                    `Espaço ID: ${formData.externalSpaceId}`
                  }
                  screen="private-office-review"
                />
              )}

            <ReviewFieldWrapper
              label="Preço mensal"
              value={priceAlert.value}
              showAlert={hasNeighborhood && priceAlert.showAlert}
              alertType={priceAlert.alertType}
              alertTitle={priceAlert.alertTitle}
              alertMessage={priceAlert.alertMessage}
              data-testid="price-review-field"
            />

            <ReviewFieldWrapper
              label=""
              value=""
              showAlert={hasNeighborhood && capacityAlert.showAlert}
              alertType={capacityAlert.alertType}
              alertTitle={capacityAlert.alertTitle}
              alertMessage={capacityAlert.alertMessage}
              data-testid="capacity-review-field"
            >
              <div className="space-y-4">
                <div>
                  <span className="text-gray-600 text-sm">Tamanho</span>
                  <p className="font-medium text-gray-900">
                    {formData.size && formData.size.trim() !== ""
                      ? `${formData.size} m²`
                      : "Não informado"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Capacidade</span>
                  <p className="font-medium text-gray-900">
                    {formData.capacity && formData.capacity.trim() !== ""
                      ? `${formData.capacity} pessoas`
                      : "Não informado"}
                  </p>
                </div>
              </div>
            </ReviewFieldWrapper>
          </div>
        </div>
      </div>
    );
  };

  const renderMedias = () => {
    const photos = formData.photos || [];
    if (photos.length === 0) return null;

    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Mídias da sala
          </h3>
          <button
            type="button"
            onClick={() => handleEditSection(1)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <Edit className="w-4 h-4" />
            Editar
          </button>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-4 gap-4">
            {photos.slice(0, 4).map((photo: any, index: number) => (
              <div
                key={index}
                className="aspect-square rounded-lg overflow-hidden bg-gray-100"
              >
                <img
                  src={photo.preview || photo.url}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          {photos.length > 4 && (
            <p className="text-sm text-gray-600 mt-2">
              +{photos.length - 4} foto(s) adicional(is)
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderAmenities = () => {
    const amenities = formData.selectedAmenities || [];
    if (amenities.length === 0) return null;
    if (!amenitiesData) return null;

    const amenityMap = new Map();
    amenitiesData.amenities.forEach((group) => {
      group.amenities.forEach((amenity) => {
        amenityMap.set(amenity.id, {
          ...amenity,
          categoryName: CATEGORY_NAMES[group.name],
        });
      });
    });

    const groupedByCategory: Record<string, any[]> = {};

    amenities.forEach((selectedAmenity: any) => {
      const amenityData = amenityMap.get(selectedAmenity.amenityId);
      if (amenityData) {
        const categoryName = amenityData.categoryName || "Outros";
        if (!groupedByCategory[categoryName]) {
          groupedByCategory[categoryName] = [];
        }
        groupedByCategory[categoryName].push({
          ...amenityData,
          status: selectedAmenity.status,
        });
      }
    });

    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Características e Serviços
          </h3>
          <button
            type="button"
            onClick={() => handleEditSection(2)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <Edit className="w-4 h-4" />
            Editar
          </button>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-3 gap-6">
            {Object.entries(groupedByCategory).map(
              ([categoryName, categoryAmenities]) => (
                <div key={categoryName}>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {categoryName}
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {categoryAmenities.map((amenity: any, index: number) => (
                      <li key={index}>• {amenity.name}</li>
                    ))}
                  </ul>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {renderSpaceInfo()}
        {renderMedias()}
        {renderAmenities()}
      </div>
    </div>
  );
};
